import React, { useState } from "react";
import axios from "axios";
import "./TalentRegistrationPage.css";
import { UploadCloud } from "lucide-react";
import { getFieldOptions } from "../../../utils/fieldsOptions";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

export default function TalentRegistrationPage() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    field: "",
    qualification: "",
  });

  const [photo, setPhoto] = useState(null);
  const [cv, setCv] = useState(null);
  const [preview, setPreview] = useState(null);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const fieldOptions = getFieldOptions();

  // =========================
  // HANDLE INPUT CHANGE
  // =========================
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // =========================
  // PHOTO UPLOAD
  // =========================
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    setPhoto(file);
    if (file) setPreview(URL.createObjectURL(file));
  };

  // =========================
  // CV UPLOAD
  // =========================
  const handleCVChange = (e) => {
    const file = e.target.files[0];
    setCv(file);
  };

  // =========================
  // SUBMIT FORM
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.field || !formData.qualification) {
      setMessage("Please fill all required fields.");
      return;
    }

    try {
      setLoading(true);

      const data = new FormData();
      data.append("name", formData.name);
      data.append("field", formData.field);
      data.append("qualification", formData.qualification);

      if (photo) data.append("photo", photo);
      if (cv) data.append("cv", cv);

      const res = await axios.post(`${API_BASE_URL}/talent`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // ✅ Alert success and go back
      alert( "Your talent registration was submitted successfully, pending approval.");
      navigate(-1); // Go back one page

      // Reset form
      setFormData({ name: "", field: "", qualification: "" });
      setPhoto(null);
      setCv(null);
      setPreview(null);

    } catch (error) {
      console.error(error);
      setMessage("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="talent-reg-ax-page">

      {/* Go Back Button */}
      <button
        className="talent-reg-ax-back-btn"
        onClick={() => navigate(-1)}
      >
        &larr; Go Back
      </button>

      <div className="talent-reg-ax-card">

        <h1 className="talent-reg-ax-title">
          Talent Pool Registration
        </h1>

        <p className="talent-reg-ax-subtitle">
          Submit your profile to join the Talent Pool
        </p>

        <form
          onSubmit={handleSubmit}
          className="talent-reg-ax-form"
        >

          {/* NAME */}
          <div className="talent-reg-ax-form-group">
            <label>Full Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
            />
          </div>

          {/* FIELD */}
          <div className="talent-reg-ax-form-group">
            <label>Field *</label>
            <select
              name="field"
              value={formData.field}
              onChange={handleChange}
              required
            >
              <option value="">Select Field</option>
              {fieldOptions.map((field) => (
                <option key={field} value={field}>
                  {field}
                </option>
              ))}
            </select>
          </div>

          {/* QUALIFICATION */}
          <div className="talent-reg-ax-form-group">
            <label>Qualification *</label>
            <input
              type="text"
              name="qualification"
              value={formData.qualification}
              onChange={handleChange}
              placeholder="Example: BSc Computer Science"
              required
            />
          </div>

          {/* PHOTO */}
          <div className="talent-reg-ax-form-group">
            <label>Profile Photo</label>
            <label className="talent-reg-ax-upload-box">
              <UploadCloud size={22} />
              Upload Photo
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                hidden
              />
            </label>
            {preview && (
              <img
                src={preview}
                alt="preview"
                className="talent-reg-ax-photo-preview"
              />
            )}
          </div>

          {/* CV */}
          <div className="talent-reg-ax-form-group">
            <label>Upload CV</label>
            <label className="talent-reg-ax-upload-box">
              <UploadCloud size={22} />
              Upload CV
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleCVChange}
                hidden
              />
            </label>
            {cv && (
              <p className="talent-reg-ax-file-name">{cv.name}</p>
            )}
          </div>

          {/* BUTTON */}
          <button
            type="submit"
            className="talent-reg-ax-submit-btn"
            disabled={loading}
          >
            {loading ? "Submitting, please wait..." : "Submit Talent"}
          </button>

          {message && (
            <div className="talent-reg-ax-form-message">{message}</div>
          )}

        </form>
      </div>
    </div>
  );
}